import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import useFinOptions from "../../Fin/useFinOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint } from "../../Fin/gl/glUi";
import { run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, FormDrawer, LoadState, PrimaryButton, formatDate, inputStyle, useLoad } from "../../../components/ui";

// The stock tabs of Parts Sales: part availability by warehouse (reserved / free, with the part data:
// OEM number, supersession, VAT code, bin) and back orders. Warehouses, transfers and stock takes are
// managed in Inventory.

function PartInfoDrawer({ row, onHide, onDone }) {
  const { data, loading } = useLoad(() => dmsService.getPartInfo(row.item_id), [row.item_id]);
  const fin = useFinOptions(["tax-rates"]);
  const whs = useLoad(() => dmsService.listWarehouses({ status: "ACTIVE" }), []);
  if (loading || !data) return null;
  const info = data.info || {};
  return (
    <FormDrawer
      show
      title={`${data.item.sku} – ${data.item.name}`}
      initial={{
        oem_part_number: info.oem_part_number || "",
        superseded_by_sku: data.superseded_by ? data.superseded_by.sku : "",
        applicability: info.applicability || "",
        tax_rate_code: info.tax_rate_code || "",
        is_returnable: info.is_returnable === undefined ? true : info.is_returnable,
        min_qty: info.min_qty ?? "",
        max_qty: info.max_qty ?? "",
        warehouse_id: null,
        bin_location: "",
      }}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean) =>
        dmsService.savePartInfo(row.item_id, {
          ...clean,
          superseded_by_sku: clean.superseded_by_sku || "",
          min_qty: clean.min_qty === "" || clean.min_qty === null ? null : Number(clean.min_qty),
          max_qty: clean.max_qty === "" || clean.max_qty === null ? null : Number(clean.max_qty),
          bin_location: clean.warehouse_id ? clean.bin_location || null : undefined,
        })
      }
      fields={[
        { key: "oem_part_number", label: "OEM part number", max: 60 },
        { key: "superseded_by_sku", label: "Replaced by (SKU)", max: 64, hint: data.replaces.length ? `Replaces ${data.replaces.map((r) => r.sku).join(", ")}` : "Leave blank while this part is current" },
        { key: "applicability", label: "Fits (models / years)", max: 500 },
        { key: "tax_rate_code", label: "VAT code", type: "select", options: [{ label: "Standard (AE_STD)", value: "" }, ...(fin["tax-rates"] || []).map((t) => ({ label: t.label, value: t.code }))] },
        { key: "is_returnable", label: "Customers can return it", type: "checkbox" },
        { key: "min_qty", label: "Minimum stock", type: "number", step: "any", min: 0 },
        { key: "max_qty", label: "Maximum stock", type: "number", step: "any", min: 0 },
        { key: "warehouse_id", label: "Bin in warehouse", type: "select", options: (whs.data || []).map((w) => ({ label: `${w.code} – ${w.name}`, value: w.id })) },
        { key: "bin_location", label: "Bin location", max: 40 },
      ]}
    />
  );
}

export function PartsStock() {
  const [q, setQ] = useState("");
  const whs = useLoad(() => dmsService.listWarehouses({}), []);
  const [wh, setWh] = useState(null);
  const { data, loading, error, reload } = useLoad(() => dmsService.partsStock({ q: q || undefined, warehouse_id: wh || undefined }), [q, wh]);
  const [editing, setEditing] = useState(null);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="SKU or name" aria-label="Search parts" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 240 }}>
          <AppDropdown id="ps-wh" value={wh} options={[{ label: "All warehouses", value: null }, ...(whs.data || []).map((w) => ({ label: `${w.code} – ${w.name}`, value: w.id }))]} onChange={(e) => setWh(e.value)} />
        </div>
        <span className="d-flex gap-3 align-items-center" style={{ marginLeft: "auto", fontSize: 14 }}>
          <Link to="/inventory/transfers">Move stock (Inventory → Transfers)</Link>
          <Link to="/inventory/stock">All stock</Link>
        </span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          rowKey={(r) => `${r.sku}-${r.warehouse_code}`}
          onRowClick={(r) => setEditing(r)}
          empty={<EmptyState title="No stock" subtitle="Parts appear here once they are received into a warehouse." />}
          columns={[
            { label: "SKU", render: (r) => <b>{r.sku}</b> },
            { label: "Part", render: (r) => `${r.item_name}${r.superseded ? " (superseded)" : ""}` },
            { label: "OEM no.", render: (r) => r.oem_part_number || "" },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "Bin", render: (r) => r.bin_location || "" },
            { label: "On hand", num: true, render: (r) => (r.below_min ? <b style={{ color: "#B91C1C" }}>{r.qty}</b> : r.qty) },
            { label: "Reserved", num: true, render: (r) => r.reserved },
            { label: "Free", num: true, render: (r) => r.available },
            { label: "Back ordered", num: true, render: (r) => r.back_ordered || "" },
            { label: "Avg cost", num: true, render: (r) => fmtMoney(r.purchase_cost) },
          ]}
        />
        <div style={hint}>Red: below the part's minimum stock. Click a part to edit its OEM number, supersession, VAT code and bin.</div>
      </LoadState>
      {editing ? <PartInfoDrawer row={editing} onHide={() => setEditing(null)} onDone={reload} /> : null}
    </>
  );
}

export function BackOrders({ onOpenOrder }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.listBackOrders({}), []);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div style={hint}>Back orders are reserved automatically when parts are received. Use this after an adjustment or a transfer in.</div>
        <PrimaryButton onClick={() => run(() => dmsService.allocateBackOrders(), reload)}>Allocate stock now</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => onOpenOrder(r.so_id)}
          empty={<EmptyState title="No open back orders" />}
          columns={[
            { label: "Order", render: (r) => <b>{r.so_number}</b> },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Part", render: (r) => `${r.sku} – ${r.item_name}` },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "Back ordered", num: true, render: (r) => Number(r.quantity) },
            { label: "Filled", num: true, render: (r) => Number(r.filled_qty) },
            { label: "Since", render: (r) => formatDate(r.created_at) },
          ]}
        />
      </LoadState>
    </>
  );
}
